import type { z } from 'zod'
import { logSecurityEvent } from '@/shared/lib/security-logger'
import { request, type RequestConfig, type ApiResponse } from './request'
import { apiResponseSchema } from './api-schemas'
import { ApiError } from './api-error'

/**
 * Drift-aware fetch: validates the response payload against a Zod schema
 * before returning it.
 *
 * Failure-mode policy (FIX_BACKLOG #9 / zod-response-validation.md PR-1):
 *
 *   • DEV / test  — LOUD. A schema mismatch throws `SchemaParseError`
 *     (plus a console.error with the issues + raw payload in DEV) so
 *     drift is caught at development time.
 *   • PROD        — OBSERVE-ONLY. The mismatch is logged via the
 *     security-logger ring buffer (`schema-mismatch` event) and the raw
 *     payload is returned AS-IS. Schemas can therefore land incrementally
 *     (PR-2..N coverage waves) without a backend drift breaking prod.
 *
 * Passing responses behave identically in all modes.
 *
 * Why a wrapper instead of inlining `schema.parse` per caller:
 * - one place to control DEV vs PROD behaviour (the policy above)
 * - one place to log the offending payload + endpoint for triage
 * - cleaner consumer code (`typedRequest(schema, config)` reads as
 *   "fetch and validate") than juggling `request().then(schema.parse)`
 *
 * @example
 *   import { typedRequest } from '@/shared/api'
 *   import { idNameListSchema, apiResponseSchema } from '@/shared/api'
 *
 *   const res = await typedRequest(
 *     idNameListSchema,
 *     { url: '/v1/measures/measure-id-name' },
 *   )
 *   // res.data is typed as IdName[] | undefined; res.success: boolean
 */
export class SchemaParseError extends ApiError {
  override name = 'SchemaParseError'
  constructor(
    public readonly url: string,
    public readonly issues: z.core.$ZodIssue[],
    raw: unknown,
  ) {
    super(`Schema mismatch on ${url}: ${issues.length} issue(s)`, {
      kind: 'schema',
      raw,
    })
  }
}

/**
 * Resolved per call (not at module load) so tests can flip the policy with
 * `vi.stubEnv`. Vitest runs with MODE='test' — kept loud there so unit and
 * contract tests fail on drift exactly like DEV.
 */
function isObserveOnly(): boolean {
  return import.meta.env.PROD && import.meta.env.MODE !== 'test'
}

export async function typedRequest<S extends z.ZodTypeAny>(
  schema: S,
  config: RequestConfig,
): Promise<ApiResponse<z.infer<S>>> {
  const raw = await request(config)
  return parseWithSchema(apiResponseSchema(schema), raw, {
    url: config.url ?? '<unknown>',
  }) as ApiResponse<z.infer<S>>
}

/**
 * Schema-validate a raw value with the same failure-mode policy as
 * `typedRequest` (loud in DEV/test, observe-only in PROD), but without
 * performing the fetch.
 *
 * Use this when an endpoint deviates from the standard `apiResponseSchema`
 * envelope — e.g. `/me` returns `{success, meta: User}` instead of
 * `{success, data: T}` — so you compose your own envelope schema and
 * just need the parse step.
 */
export function parseWithSchema<S extends z.ZodTypeAny>(
  schema: S,
  raw: unknown,
  context: { url: string },
): z.infer<S> {
  const parsed = schema.safeParse(raw)
  if (!parsed.success) {
    if (isObserveOnly()) {
      // Observe-only: record the drift (paths only — no payload values, the
      // ring buffer is reachable from devtools) and trust the raw payload.
      logSecurityEvent('schema-mismatch', {
        url: context.url,
        issueCount: parsed.error.issues.length,
        issuePaths: parsed.error.issues
          .slice(0, 10)
          .map((issue) => `${issue.path.join('.')}: ${issue.code}`),
      })
      return raw as z.infer<S>
    }
    if (import.meta.env.DEV) {
      console.error(
        `[parseWithSchema] schema mismatch for ${context.url}:`,
        parsed.error.issues,
        '\nraw payload:',
        raw,
      )
    }
    throw new SchemaParseError(context.url, parsed.error.issues, raw)
  }
  return parsed.data
}
